/**
 * Секція «Користувачі і доступи» у AdminPage (Порція Виробництво.5 UX).
 *
 * Раніше було двома сторінками:
 *  - /team — запрошення, активні, призупинені, ролі (через RolesSection)
 *  - /admin tab «Користувачі» — створення вручну + деактивація
 *  - /admin tab «Ролі» — read-only таблиця (примітивна, дублювала Team)
 *
 * Тут зливаємо у одну зону:
 *  - Активні / Запрошені / Призупинені (з Team)
 *  - Створення вручну (з Admin UsersTab) — для випадків без email-запрошення
 *
 * Ролі тепер мають окремий tab у AdminPage з повноцінним RolesSection
 * (заміна примітивної таблиці). Це закриває дублювання «правити ролі
 * лише з Team».
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

/**
 * Витягує читабельне повідомлення з помилки axios.
 *
 * Покриває 3 формати detail з FastAPI:
 *  - string  (Pydantic 1.x style + ручний HTTPException)
 *  - object  (рідко: {code, message})
 *  - array   (Pydantic 2.x 422 validation: [{loc, msg, type}, ...])
 *
 * Без цього `alert(detail)` для 422 повертає "[object Object]".
 */
function extractApiError(e: unknown, fallback = 'Помилка'): string {
  const err = e as AxiosError<{ detail?: unknown }>;
  const detail = err.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        const obj = d as { loc?: unknown[]; msg?: string; type?: string };
        const field = Array.isArray(obj.loc) ? obj.loc.slice(-1).join('') : '';
        return field ? `${field}: ${obj.msg ?? obj.type ?? 'invalid'}` : (obj.msg ?? '');
      })
      .filter(Boolean)
      .join('; ') || fallback;
  }
  if (detail && typeof detail === 'object') {
    return JSON.stringify(detail);
  }
  return err.message || fallback;
}
import {
  listMemberships,
  listInvitations,
  suspendMembership,
  resumeMembership,
  deleteMembership,
  revokeInvitation,
  type MembershipListItem,
  type InvitationListItem,
} from '@/api/invitations';
import { usersAdmin, type AdminUserCreate } from '@/api/admin';
import { useConfirm } from '@/components/ConfirmProvider';
import RequirePermission from '@/auth/RequirePermission';
import InviteUserDialog from '../team/InviteUserDialog';

export default function TeamSection() {
  const [inviteOpen, setInviteOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  const active = useQuery({
    queryKey: ['memberships', 'ACTIVE'],
    queryFn: () => listMemberships('ACTIVE'),
  });
  const suspended = useQuery({
    queryKey: ['memberships', 'SUSPENDED'],
    queryFn: () => listMemberships('SUSPENDED'),
  });
  const invited = useQuery({
    queryKey: ['invitations'],
    queryFn: listInvitations,
  });

  function refreshAll() {
    queryClient.invalidateQueries({ queryKey: ['memberships'] });
    queryClient.invalidateQueries({ queryKey: ['invitations'] });
  }

  function errorDetail(e: unknown, fallback: string): string {
    return extractApiError(e, fallback);
  }

  const suspendMut = useMutation({
    mutationFn: suspendMembership,
    onSuccess: () => refreshAll(),
    onError: (e) => alert(errorDetail(e, 'Не вдалося призупинити доступ')),
  });
  const resumeMut = useMutation({
    mutationFn: resumeMembership,
    onSuccess: () => refreshAll(),
    onError: (e) => alert(errorDetail(e, 'Не вдалося відновити доступ')),
  });
  const deleteMut = useMutation({
    mutationFn: deleteMembership,
    onSuccess: () => refreshAll(),
    onError: (e) => alert(errorDetail(e, 'Не вдалося видалити')),
  });
  const revokeMut = useMutation({
    mutationFn: revokeInvitation,
    onSuccess: () => refreshAll(),
    onError: (e) => alert(errorDetail(e, 'Не вдалося скасувати запрошення')),
  });

  async function onSuspend(m: MembershipListItem) {
    const ok = await confirm({
      message: `Призупинити доступ ${m.email}? Користувач не зможе входити у tenant, поки доступ не буде відновлено.`,
      okLabel: 'Призупинити',
      cancelLabel: 'Скасувати',
    });
    if (ok) suspendMut.mutate(m.id);
  }

  async function onDeleteMembership(m: MembershipListItem) {
    const ok = await confirm({
      message: `Видалити доступ ${m.email}? Цю дію неможливо скасувати. Користувач втратить доступ повністю.`,
      okLabel: 'Видалити',
      cancelLabel: 'Скасувати',
    });
    if (ok) deleteMut.mutate(m.id);
  }

  async function onRevoke(inv: InvitationListItem) {
    const ok = await confirm({
      message: `Скасувати запрошення для ${inv.email}? Посилання стане недійсним.`,
      okLabel: 'Скасувати запрошення',
      cancelLabel: 'Залишити',
    });
    if (ok) revokeMut.mutate(inv.id);
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginBottom: '1rem' }}>
        <RequirePermission code="admin.users.manage">
          <button
            type="button"
            onClick={() => setManualOpen((o) => !o)}
            title="Створити користувача вручну (без email-запрошення)"
          >
            {manualOpen ? '× Сховати' : '+ Створити вручну'}
          </button>
        </RequirePermission>
        <RequirePermission code="admin.users.manage">
          <button
            type="button"
            className="primary"
            onClick={() => setInviteOpen(true)}
          >
            + Запросити користувача
          </button>
        </RequirePermission>
      </div>

      {manualOpen && (
        <Section title="Створення користувача вручну (без email-запрошення)">
          <ManualCreateForm onDone={() => { setManualOpen(false); refreshAll(); }} />
        </Section>
      )}

      <Section title={`Активні (${active.data?.length ?? 0})`}>
        <MembershipsTable
          loading={active.isLoading}
          items={active.data ?? []}
          actions={(m) => [
            { label: 'Призупинити', onClick: () => onSuspend(m), variant: 'default' },
            { label: 'Видалити', onClick: () => onDeleteMembership(m), variant: 'danger' },
          ]}
        />
      </Section>

      <Section title={`Запрошення в очікуванні (${invited.data?.length ?? 0})`}>
        <InvitationsTable
          loading={invited.isLoading}
          items={invited.data ?? []}
          onRevoke={onRevoke}
        />
      </Section>

      <Section title={`Призупинені (${suspended.data?.length ?? 0})`} collapsedByDefault>
        <MembershipsTable
          loading={suspended.isLoading}
          items={suspended.data ?? []}
          actions={(m) => [
            { label: 'Відновити', onClick: () => resumeMut.mutate(m.id), variant: 'default' },
            { label: 'Видалити', onClick: () => onDeleteMembership(m), variant: 'danger' },
          ]}
        />
      </Section>

      {inviteOpen && (
        <InviteUserDialog
          onClose={() => setInviteOpen(false)}
          onCreated={() => {
            setInviteOpen(false);
            refreshAll();
          }}
        />
      )}
    </div>
  );
}

// =============================================================================
// Manual user create form (was AdminPage.UsersTab)
// =============================================================================

function ManualCreateForm({ onDone }: { onDone: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState<AdminUserCreate>({
    email: '', password: '', full_name: '', tenant_role: 'MEMBER',
  });
  const create = useMutation({
    mutationFn: () => usersAdmin.create(form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['memberships'] });
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      setForm({ email: '', password: '', full_name: '', tenant_role: 'MEMBER' });
      onDone();
    },
    onError: (e) => alert(extractApiError(e)),
  });
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr) auto', gap: '0.5rem' }}>
      <input placeholder="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
      <input placeholder="ПІБ" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
      <input placeholder="пароль" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
      <select value={form.tenant_role} onChange={(e) => setForm({ ...form, tenant_role: e.target.value as AdminUserCreate['tenant_role'] })}>
        <option value="MEMBER">MEMBER</option>
        <option value="ADMIN">ADMIN</option>
        <option value="OWNER">OWNER</option>
      </select>
      <button
        type="button"
        className="primary"
        onClick={() => create.mutate()}
        disabled={!form.email || !form.password || !form.full_name || create.isPending}
      >
        Створити
      </button>
    </div>
  );
}

// =============================================================================
// Section (collapsible)
// =============================================================================

function Section({
  title, children, collapsedByDefault = false,
}: {
  title: string;
  children: React.ReactNode;
  collapsedByDefault?: boolean;
}) {
  const [open, setOpen] = useState(!collapsedByDefault);
  return (
    <div className="card" style={{ marginTop: '1rem' }}>
      <div
        onClick={() => setOpen((o) => !o)}
        style={{
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <h3 style={{ margin: 0 }}>{title}</h3>
        <span style={{ color: 'var(--text-secondary)' }}>{open ? '▾' : '▸'}</span>
      </div>
      {open && <div style={{ marginTop: '1rem' }}>{children}</div>}
    </div>
  );
}

// =============================================================================
// Tables
// =============================================================================

interface MembershipAction {
  label: string;
  onClick: () => void;
  variant: 'default' | 'danger';
}

function MembershipsTable({
  loading, items, actions,
}: {
  loading: boolean;
  items: MembershipListItem[];
  actions: (m: MembershipListItem) => MembershipAction[];
}) {
  if (loading) return <div>Завантаження…</div>;
  if (items.length === 0) {
    return (
      <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
        Список порожній.
      </div>
    );
  }
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
          <th style={{ padding: '0.5rem' }}>Email</th>
          <th style={{ padding: '0.5rem' }}>Ім'я</th>
          <th style={{ padding: '0.5rem' }}>Роль</th>
          <th style={{ padding: '0.5rem' }}>Статус</th>
          <th style={{ padding: '0.5rem', textAlign: 'right' }}>Дії</th>
        </tr>
      </thead>
      <tbody>
        {items.map((m) => (
          <tr key={m.id} style={{ borderBottom: '1px solid var(--border-soft)' }}>
            <td style={{ padding: '0.5rem' }}>{m.email}</td>
            <td style={{ padding: '0.5rem' }}>{m.full_name}</td>
            <td style={{ padding: '0.5rem' }}>{m.tenant_role}</td>
            <td style={{ padding: '0.5rem' }}>
              {m.status}
              {m.suspended_by_email && (
                <span style={{ marginLeft: '0.4rem', color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                  (від {m.suspended_by_email})
                </span>
              )}
            </td>
            <td style={{ padding: '0.5rem', textAlign: 'right' }}>
              {actions(m).map((a, i) => (
                <button
                  key={i}
                  type="button"
                  className={a.variant === 'danger' ? 'danger' : ''}
                  style={{ marginLeft: '0.4rem' }}
                  onClick={a.onClick}
                >
                  {a.label}
                </button>
              ))}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function InvitationsTable({
  loading, items, onRevoke,
}: {
  loading: boolean;
  items: InvitationListItem[];
  onRevoke: (i: InvitationListItem) => void;
}) {
  if (loading) return <div>Завантаження…</div>;
  if (items.length === 0) {
    return (
      <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
        Немає запрошень в очікуванні.
      </div>
    );
  }
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
          <th style={{ padding: '0.5rem' }}>Email</th>
          <th style={{ padding: '0.5rem' }}>Роль</th>
          <th style={{ padding: '0.5rem' }}>Запрошено</th>
          <th style={{ padding: '0.5rem', textAlign: 'right' }}>Дії</th>
        </tr>
      </thead>
      <tbody>
        {items.map((inv) => (
          <tr key={inv.id} style={{ borderBottom: '1px solid var(--border-soft)' }}>
            <td style={{ padding: '0.5rem' }}>{inv.email}</td>
            <td style={{ padding: '0.5rem' }}>{inv.tenant_role}</td>
            <td style={{ padding: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
              {inv.invited_at ? new Date(inv.invited_at).toLocaleDateString('uk-UA') : '—'}
            </td>
            <td style={{ padding: '0.5rem', textAlign: 'right' }}>
              <button type="button" className="danger" onClick={() => onRevoke(inv)}>
                Скасувати
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
